import type { FullBook, FullBookContentResource, ZyServerSection } from './zybooks-api';
import { mapContentResource } from './zybooks-api';

/**
 * Flattened content resource with chapter/section info denormalized for comparison
 */
export interface FlattenedContentResource extends FullBookContentResource {
    chapter_number: number;
    chapter_title: string;
    section_number: number;
    section_title: string;
    cr_ordering: number;
}

/**
 * Flatten a hierarchical FullBook structure into an array of content resources
 * with chapter/section info denormalized on each resource
 */
export function flattenFullBook(book: FullBook): FlattenedContentResource[] {
    const resources: FlattenedContentResource[] = [];

    for (const chapter of book.chapters) {
        for (const section of chapter.sections) {
            for (let i = 0; i < section.content_resources.length; i++) {
                const cr = section.content_resources[i];
                resources.push({
                    ...cr,
                    chapter_number: chapter.number,
                    chapter_title: chapter.title,
                    section_number: section.number,
                    section_title: section.title,
                    cr_ordering: i + 1,
                });
            }
        }
    }

    // Also include unused sections
    for (const section of book.unused) {
        for (let i = 0; i < section.content_resources.length; i++) {
            const cr = section.content_resources[i];
            resources.push({
                ...cr,
                chapter_number: -1, // Unused chapter
                chapter_title: 'Unused',
                section_number: section.number,
                section_title: section.title,
                cr_ordering: i + 1,
            });
        }
    }

    return resources;
}

/**
 * Flatten a single section's content resources into FlattenedContentResource array
 * More efficient than flattenFullBook when only comparing specific sections
 */
export function flattenSection(section: ZyServerSection): FlattenedContentResource[] {
    return section.content_resources.map((cr, index) => ({
        ...mapContentResource(cr),
        chapter_number: section.canonical_chapter_number,
        chapter_title: `Chapter ${section.canonical_chapter_number}`,
        section_number: section.canonical_section_number,
        section_title: section.title,
        cr_ordering: index + 1,
    }));
}

export const ALL_COMPARABLE_FIELDS = [
    'resource_type',
    'caption',
    'instructions',
    'payload',
    'parts',
    'height',
    'width',
    'activity_type',
] as const;

export type ComparableField = (typeof ALL_COMPARABLE_FIELDS)[number];

export const IGNORE_FIELDS: ComparableField[] = [
    // Add fields here to ignore, e.g.:
    // 'payload',  // if payload has timestamps or other noise
];

export const COMPARABLE_FIELDS = ALL_COMPARABLE_FIELDS.filter(f => !IGNORE_FIELDS.includes(f));

export interface FieldDiff {
    field: ComparableField;
    leftValue: unknown;
    rightValue: unknown;
    isEqual: boolean;
}

export type ComparisonStatus = 'match' | 'difference' | 'missing_left' | 'missing_right';

export interface ContentResourceComparison {
    /** Position key (chapter-section-ordering) used for matching */
    positionKey: string;
    status: ComparisonStatus;
    left: FlattenedContentResource | null;
    right: FlattenedContentResource | null;
    fieldDiffs: FieldDiff[];
    chapterNumber: number;
    sectionNumber: number;
    ordering: number;
}

/**
 * Generate a position key for matching content resources
 * Format: "chapter-section-ordering" (e.g., "1-2-3" for chapter 1, section 2, CR #3)
 */
export function getPositionKey(resource: FlattenedContentResource): string {
    return `${resource.chapter_number}-${resource.section_number}-${resource.cr_ordering}`;
}

export interface ComparisonSummary {
    total: number;
    matches: number;
    differences: number;
    missingLeft: number;
    missingRight: number;
}

/**
 * Deep equality check for two values
 */
function deepEqual(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    if (a === null || b === null) return a === b;
    if (typeof a !== typeof b) return false;

    if (typeof a === 'object') {
        // Handle arrays
        if (Array.isArray(a) && Array.isArray(b)) {
            if (a.length !== b.length) return false;
            return a.every((item, index) => deepEqual(item, b[index]));
        }

        // Handle objects
        if (!Array.isArray(a) && !Array.isArray(b)) {
            const aKeys = Object.keys(a as Record<string, unknown>);
            const bKeys = Object.keys(b as Record<string, unknown>);
            if (aKeys.length !== bKeys.length) return false;
            return aKeys.every(key => deepEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]));
        }
    }

    return false;
}

/**
 * Parse payload string to object for comparison
 */
function parsePayload(payload: string | object | null): object | null {
    if (!payload) return null;
    if (typeof payload === 'object') return payload;
    try {
        return JSON.parse(payload);
    } catch {
        return null;
    }
}

function getFieldValue(resource: FlattenedContentResource, field: ComparableField): unknown {
    if (field === 'payload') {
        return parsePayload(resource.payload);
    }
    return resource[field as keyof FlattenedContentResource];
}

export function compareContentResources(left: FlattenedContentResource | null, right: FlattenedContentResource | null): FieldDiff[] {
    const diffs: FieldDiff[] = [];

    for (const field of COMPARABLE_FIELDS) {
        const leftValue = left ? getFieldValue(left, field) : undefined;
        const rightValue = right ? getFieldValue(right, field) : undefined;
        const isEqual = deepEqual(leftValue, rightValue);

        diffs.push({
            field,
            leftValue,
            rightValue,
            isEqual,
        });
    }

    return diffs;
}

/**
 * Compare two arrays of content resources and generate comparison results.
 *
 * Matching is done by POSITION (chapter/section/ordering), NOT by guid.
 * This is because the two books being compared are created from the same source
 * via different flows (Legacy vs Modern Authoring), so they have the same content
 * in the same positions but different GUIDs.
 */
export function compareContentResourceArrays(
    leftResources: FlattenedContentResource[],
    rightResources: FlattenedContentResource[],
): ContentResourceComparison[] {
    const comparisons: ContentResourceComparison[] = [];

    // Create maps by position for quick lookup
    const leftByPosition = new Map<string, FlattenedContentResource>();
    const rightByPosition = new Map<string, FlattenedContentResource>();

    for (const resource of leftResources) {
        leftByPosition.set(getPositionKey(resource), resource);
    }

    for (const resource of rightResources) {
        rightByPosition.set(getPositionKey(resource), resource);
    }

    // Get all unique position keys
    const allPositionKeys = new Set([...leftByPosition.keys(), ...rightByPosition.keys()]);

    for (const positionKey of allPositionKeys) {
        const left = leftByPosition.get(positionKey) || null;
        const right = rightByPosition.get(positionKey) || null;

        const fieldDiffs = compareContentResources(left, right);
        const hasDifferences = fieldDiffs.some(diff => !diff.isEqual);

        let status: ComparisonStatus;
        if (!left) {
            status = 'missing_left';
        } else if (!right) {
            status = 'missing_right';
        } else if (hasDifferences) {
            status = 'difference';
        } else {
            status = 'match';
        }

        const reference = left || right;
        comparisons.push({
            positionKey,
            status,
            left,
            right,
            fieldDiffs,
            chapterNumber: reference?.chapter_number ?? 0,
            sectionNumber: reference?.section_number ?? 0,
            ordering: reference?.cr_ordering ?? 0,
        });
    }

    // Sort by chapter, section, then ordering
    comparisons.sort((a, b) => {
        if (a.chapterNumber !== b.chapterNumber) return a.chapterNumber - b.chapterNumber;
        if (a.sectionNumber !== b.sectionNumber) return a.sectionNumber - b.sectionNumber;
        return a.ordering - b.ordering;
    });

    return comparisons;
}

export function generateComparisonSummary(comparisons: ContentResourceComparison[]): ComparisonSummary {
    return {
        total: comparisons.length,
        matches: comparisons.filter(c => c.status === 'match').length,
        differences: comparisons.filter(c => c.status === 'difference').length,
        missingLeft: comparisons.filter(c => c.status === 'missing_left').length,
        missingRight: comparisons.filter(c => c.status === 'missing_right').length,
    };
}

export function filterBySection(
    resources: FlattenedContentResource[],
    chapterNumber: number,
    sectionNumber: number,
): FlattenedContentResource[] {
    return resources.filter(r => r.chapter_number === chapterNumber && r.section_number === sectionNumber);
}

export function getUniqueSections(
    resources: FlattenedContentResource[],
): Array<{ chapterNumber: number; sectionNumber: number; title: string }> {
    const sectionMap = new Map<string, { chapterNumber: number; sectionNumber: number; title: string }>();

    for (const resource of resources) {
        const key = `${resource.chapter_number}-${resource.section_number}`;
        if (!sectionMap.has(key)) {
            sectionMap.set(key, {
                chapterNumber: resource.chapter_number,
                sectionNumber: resource.section_number,
                title: resource.section_title,
            });
        }
    }

    return Array.from(sectionMap.values()).sort((a, b) => {
        if (a.chapterNumber !== b.chapterNumber) return a.chapterNumber - b.chapterNumber;
        return a.sectionNumber - b.sectionNumber;
    });
}

export function formatValueForDisplay(value: unknown): string {
    if (value === null || value === undefined) {
        return 'null';
    }
    if (typeof value === 'object') {
        return JSON.stringify(value, null, 2);
    }
    return String(value);
}
